import React, { FC } from 'react'
import MediaQuery from 'react-responsive'
import { Swipeable } from 'react-swipeable'

import {
  ArrowButton,
  BasicDoughnut,
  Direction,
  DoughnutContent,
  SplitContent,
  SplitContentProps,
  theme,
} from '@acorns/web-components'

import PageIndicator from 'components/PageIndicator'
import {
  Portfolio,
  PortfolioTheme,
  Suitability_Portfolio,
} from 'generated/types'
import Grid from 'pages/invest/Grid'
import { useCrypto } from 'pages/portfolio/Crypto'
import {
  PORTFOLIO_ARROW,
  PORTFOLIO_BONDS_PERCENTAGE,
  PORTFOLIO_CRYPTO_PERCENTAGE,
  PORTFOLIO_DESCRIPTION,
  PORTFOLIO_ETF,
  PORTFOLIO_STOCKS_PERCENTAGE,
  PORTFOLIO_VALUE,
} from 'utils/testing/testIDs'

import { usePortfolioContent } from '../hooks/usePortfolioContent'
import { Relative, StyledTabs } from '../styled-components'
import { RootProps } from '../types'
import ConsiderationsContent from './ChangeRiskComponents/ConsiderationsContent'
import RecommendedRiskContent from './ChangeRiskComponents/RecommendedRiskContent'
import { LoadingContainer } from './LoadingContainer'

const ZERO_PERCENT = '0%'

const isArrowDisplayed = (portfolio: Portfolio, direction: string) => {
  if (direction === 'left') {
    if (portfolio.name !== 'Conservative' && portfolio.theme === 'STANDARD') {
      return true
    } else if (
      portfolio.name !== 'Moderately Conservative' &&
      portfolio.theme === 'SUSTAINABLE'
    ) {
      return true
    }
  } else if (direction === 'right') {
    if (portfolio.name !== 'Aggressive' && portfolio.theme === 'STANDARD') {
      return true
    } else if (
      portfolio.name !== 'Aggressive' &&
      portfolio.theme === 'SUSTAINABLE'
    ) {
      return true
    }
  }
  return false
}

export type ChangeRiskContentProps = RootProps & {
  portfolio: Portfolio
  recommendedPortfolio?: Suitability_Portfolio | null
  selectedIndex?: number
  setSelectedIndex?: (selectedIndex: number) => void
  setIsConservativeSusModalOpen?: (isConservativeSusModalOpen: boolean) => void
}

export const ChangeRiskContent: FC<ChangeRiskContentProps> = ({
  header,
  portfolio,
  portfolios = [],
  recommendedPortfolio,
  setIsConservativeSusModalOpen,
  setSelectedIndex = () => {},
  selectedIndex = 0,
  onETFPress,
  graphData,
  selectedView,
}) => {
  const { currentPortfolioIncludesCrypto } = useCrypto()

  const {
    selectedDataIndex,
    totalBondsPercentage,
    totalStocksPercentage,
    totalCryptoPercentage,
    handleSelectedDataChange,
    chartData,
    chartArcColors,
  } = usePortfolioContent({ portfolio, graphData, selectedView })

  const hasBonds = totalBondsPercentage !== ZERO_PERCENT
  const hasStocks = totalStocksPercentage !== ZERO_PERCENT

  const isRecommendedRisk = recommendedPortfolio?.name === portfolio?.name

  const nextPortfolio = () => {
    if (selectedIndex + 1 === (portfolios || []).length) {
      setSelectedIndex(0)
    } else {
      setSelectedIndex(selectedIndex + 1)
    }
  }

  const prevPortfolio = () => {
    if (selectedIndex === 0) {
      const portfolioIndex = (portfolios || []).length - 1
      setSelectedIndex(portfolioIndex)
    } else {
      setSelectedIndex(selectedIndex - 1)
    }
  }

  const handlePageIndicatorClick = (index: number) => {
    setSelectedIndex(index)
  }

  const handleGoToRecommendedPortfolio = () => {
    if (
      recommendedPortfolio?.name === 'Conservative' &&
      portfolio.theme === PortfolioTheme.Sustainable &&
      setIsConservativeSusModalOpen
    ) {
      setIsConservativeSusModalOpen(true)
    } else {
      setSelectedIndex(
        portfolios.findIndex(
          (element: any) => element?.name === recommendedPortfolio?.name,
        ),
      )
    }
  }

  const PORTFOLIO_RISK_OPTIONS = [
    {
      label: 'Overview',
      content: (
        <>
          <RecommendedRiskContent
            isRecommendedRisk={isRecommendedRisk}
            handleGoToRecommendedPortfolio={handleGoToRecommendedPortfolio}
          />
          <Grid
            testIdPrefix={PORTFOLIO_ETF}
            items={portfolio?.portfolioSecurityAllocations || []}
            onItemPress={onETFPress}
            selectedIndex={selectedDataIndex}
            mobilePadding={0}
            hasPercentage={true}
          />
        </>
      ),
    },
    {
      label: 'Considerations',
      content: (
        <ConsiderationsContent risk={portfolio?.riskLevel?.toLowerCase()} />
      ),
    },
  ]

  const getDoughnutContent = () => {
    if (currentPortfolioIncludesCrypto && hasStocks && hasBonds) {
      const splitContentProps: SplitContentProps = {
        topContent: [
          {
            label: 'Crypto',
            value: totalCryptoPercentage || '',
            bold: true,
            color: theme.colors.purple,
            testId: PORTFOLIO_CRYPTO_PERCENTAGE,
          },
        ],
        bottomContent: [
          {
            label: 'Stocks',
            value: totalStocksPercentage,
            bold: true,
            color: theme.colors.greenDark,
            testId: PORTFOLIO_STOCKS_PERCENTAGE,
          },

          {
            label: 'Bonds',
            value: totalBondsPercentage,
            bold: true,
            testId: PORTFOLIO_BONDS_PERCENTAGE,
          },
        ],
      }
      return <SplitContent {...splitContentProps} />
    } else if (currentPortfolioIncludesCrypto) {
      const splitContentProps: SplitContentProps = {
        topContent: [
          {
            label: 'Crypto',
            value: totalCryptoPercentage || '',
            bold: true,
            color: theme.colors.purple,
            testId: PORTFOLIO_CRYPTO_PERCENTAGE,
          },
        ],
        bottomContent: [
          {
            label: hasStocks ? 'Stocks' : 'Bonds',
            value: hasStocks ? totalStocksPercentage : totalBondsPercentage,
            bold: true,
            color: hasStocks ? theme.colors.greenDark : theme.colors.green,
            testId: hasStocks
              ? PORTFOLIO_STOCKS_PERCENTAGE
              : PORTFOLIO_BONDS_PERCENTAGE,
          },
        ],
      }
      return <SplitContent {...splitContentProps} />
    } else {
      const splitContentProps: SplitContentProps = {
        topContent: [
          {
            label: 'Bonds',
            value: totalBondsPercentage,
            testId: PORTFOLIO_BONDS_PERCENTAGE,
          },
        ],
        bottomContent: [
          {
            label: 'Stocks',
            value: totalStocksPercentage,
            color: theme.colors.greenDark,
            testId: PORTFOLIO_STOCKS_PERCENTAGE,
          },
        ],
      }
      return <SplitContent {...splitContentProps} />
    }
  }

  return (
    <LoadingContainer portfolios={portfolios}>
      {header}
      <Swipeable
        style={{ width: '100%' }}
        onSwipedLeft={nextPortfolio}
        onSwipedRight={prevPortfolio}
      >
        <Relative>
          <BasicDoughnut
            testId={PORTFOLIO_DESCRIPTION}
            data={chartData}
            colors={chartArcColors}
            onItemHighlight={handleSelectedDataChange}
            options={{
              animation: {
                animateRotate: true,
                animateScale: false,
              },
              round: true,
            }}
          >
            <DoughnutContent
              testId={PORTFOLIO_VALUE}
              defaultContent={getDoughnutContent()}
            />
          </BasicDoughnut>
          {portfolios.length > 1 && (
            <MediaQuery minWidth={600}>
              {isArrowDisplayed(portfolio, 'left') && (
                <ArrowButton
                  direction={Direction.Left}
                  onClick={prevPortfolio}
                  data-testid={PORTFOLIO_ARROW + '_left'}
                />
              )}
              {isArrowDisplayed(portfolio, 'right') && (
                <ArrowButton
                  direction={Direction.Right}
                  onClick={nextPortfolio}
                  data-testid={PORTFOLIO_ARROW + '_right'}
                />
              )}
            </MediaQuery>
          )}
        </Relative>
        {portfolios.length > 1 && (
          <PageIndicator
            count={portfolios.length}
            onItemClick={handlePageIndicatorClick}
            selected={selectedIndex || 0}
          />
        )}
        <StyledTabs options={PORTFOLIO_RISK_OPTIONS} />
      </Swipeable>
    </LoadingContainer>
  )
}
